'use client';

import { useMemo, type ComponentProps } from 'react';
import { Link, LucideIcon } from '@skin-seoul/design-system';
import {
  formatCurrency,
  getCartPackOptions,
  getCartPackUpgrade,
  ProductType,
  useProductService,
} from '@skin-seoul/service';
import { extractPathname } from '@skin-seoul/utils';
import CartProductCard from './CartProductCard';
import { CartAnalyticsImpression, useCartAnalytics } from './CartAnalytics';

type Props = ComponentProps<typeof CartProductCard>;

function ProductPackCard({ slug, ...props }: Props & { slug: string }) {
  const { track } = useCartAnalytics();
  const LinkComponent = props.linkComponent || Link;
  const normalizedProductLink = extractPathname(props.item.productLink);
  const { useGetProductsDetailWithQuery } = useProductService();
  // Intentional inline-query exception: keep the same stateful cart row mounted while
  // optional detail loads/refetches. A Suspense row fallback would reset quantity editing
  // and the user's requested stable size and pack label.
  // The outer variation gate limits this fetch; the cart itself already has its own boundary.
  const query = useGetProductsDetailWithQuery({ slug, currency: props.currency });
  const product = query.data?.product;
  const sku = props.item.sku;
  const { savingsPercent, upgrade } = useMemo(() => {
    if (!product) {
      return { savingsPercent: undefined, upgrade: undefined };
    }
    return {
      savingsPercent: getCartPackOptions(product, sku).find((option) => option.sku === sku)?.savingsPercent,
      upgrade: getCartPackUpgrade(product, sku),
    };
  }, [product, sku]);
  const nudgeParams = {
    product_id: props.item.productId,
    sku,
    recommended_sku: upgrade?.sku,
    target_pack: upgrade?.label,
    unit_price: upgrade?.unitPrice,
  };
  return (
    <CartProductCard
      {...props}
      packSavingsPercent={savingsPercent}
      packNudge={
        upgrade?.isAvailable &&
        upgrade.unitPrice !== null && (
          <CartAnalyticsImpression
            impressionKey={`bundle:${props.item.key}:${upgrade.sku}`}
            onImpression={() => track('bundle_nudge_viewed', nudgeParams)}
          >
            <div className="mt-4 flex items-center justify-between gap-2 rounded-lg bg-primary-50 p-2">
              <p className="text-left font-normal text-paragraph-xsmall text-neutral-700">
                <span className="font-semibold text-primary-500">Add 1 pack</span> to buy at{' '}
                <span className="font-semibold text-primary-500">
                  {formatCurrency(props.currency, upgrade.unitPrice)}
                </span>{' '}
                per unit
              </p>
              <LinkComponent
                href={normalizedProductLink}
                onClick={() => {
                  track('bundle_nudge_clicked', { ...nudgeParams, action: 'view_product_options' });
                  props.onImageClick?.(normalizedProductLink);
                }}
                className="flex shrink-0 items-center gap-0.5 font-medium text-label-small text-neutral-400 no-underline hover:no-underline"
                aria-label={`Change ${props.item.name} options`}
              >
                Change
                <LucideIcon name="ChevronRight" size={16} />
              </LinkComponent>
            </div>
          </CartAnalyticsImpression>
        )
      }
    />
  );
}

export function CartProductCardWithPacks(props: Props) {
  const supportsPack = [ProductType.Variation, ProductType.Variable].includes(props.item.type);
  const slug = extractPathname(props.item.productLink).split(/[?#]/)[0].split('/').filter(Boolean).at(-1);
  return supportsPack && slug ? <ProductPackCard {...props} slug={slug} /> : <CartProductCard {...props} />;
}
